import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TenantFeePolicyApi } from "../apis";
import type { FeePolicy, FeePolicyDraft, FeePolicyTier } from "../apis/tenantFeePolicy";
import { resolveBillingError } from "../utils/billing-errors";
import { Message } from "../utils/message";

const EDITOR_BASE = "/my-billing/fee-policy/editor";

function isDraftExists(e: unknown): boolean {
  const err = e as { code?: string; message?: string } | undefined;
  return `${err?.code ?? ""} ${err?.message ?? ""}`.includes("BILLING_POLICY_DRAFT_EXISTS");
}

/** Server stores tierConfig as { tiers: [...] }; the draft payload wants a flat array. */
function extractTiers(tierConfig: unknown): FeePolicyTier[] | null {
  if (Array.isArray(tierConfig)) return tierConfig as FeePolicyTier[];
  const t = (tierConfig as { tiers?: FeePolicyTier[] } | null)?.tiers;
  return Array.isArray(t) ? t : null;
}

/** Copy a live policy's full config into a new amend-draft payload (linked by sourcePolicyId). */
function buildAmendBody(p: FeePolicy): FeePolicyDraft {
  return {
    scope                         : p.scope,
    name                          : p.name,
    calculationMethod             : p.calculationMethod,
    feeRate                       : p.feeRate,
    flatFeeAmount                 : p.flatFeeAmount,
    tierConfig                    : extractTiers(p.tierConfig),
    minFeeAmount                  : p.minFeeAmount,
    maxFeeAmount                  : p.maxFeeAmount,
    feeBasis                      : p.feeBasis,
    deductionMode                 : p.deductionMode,
    refundPolicy                  : p.refundPolicy,
    absorbPlatformFee             : p.absorbPlatformFee,
    chargeEndCustomerFee          : p.chargeEndCustomerFee,
    feeCurrency                   : p.feeCurrency,
    applicableOrderTypes          : p.applicableOrderTypes,
    applicableCurrencies          : p.applicableCurrencies,
    excludedTradingRelationshipIds: p.excludedTradingRelationshipIds,
    effectiveUntil                : p.effectiveUntil,
    sourcePolicyId                : p.id,
  };
}

/**
 * Existing amend working-copy for this source, if one is open. A future-dated amend
 * lives as a SCHEDULED row (not DRAFT), so both statuses are considered.
 */
async function findAmendWorkingCopy(sourceId: string): Promise<FeePolicy | null> {
  const res = await TenantFeePolicyApi.list({ page: 1, limit: 200 });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: FeePolicy[] = Array.isArray(res) ? res : ((res as any)?.rows ?? []);
  return rows.find(
    (r) => r.sourcePolicyId === sourceId && (r.status === "DRAFT" || r.status === "SCHEDULED")
  ) ?? null;
}

/** A DRAFT amend reopens in the editor; a SCHEDULED amend opens its detail (to view/cancel). */
function workingCopyTarget(wc: FeePolicy): string {
  return wc.status === "SCHEDULED"
    ? `/my-billing/fee-policy/${wc.id}`
    : `${EDITOR_BASE}/${wc.id}`;
}

/**
 * Opens a policy for editing. A DRAFT edits itself; a live (ACTIVE/SUSPENDED/
 * SCHEDULED) policy is edited through an amend working-copy DRAFT linked by
 * sourcePolicyId — reusing an existing amend draft if one is already open, and
 * recovering from a concurrent-amend race (BILLING_POLICY_DRAFT_EXISTS).
 */
export function useEditPolicy() {
  const navigate = useNavigate();
  return useCallback(
    async (policy: FeePolicy) => {
      if (policy.status === "DRAFT") {
        navigate(`${EDITOR_BASE}/${policy.id}`);
        return;
      }
      try {
        const existing = await findAmendWorkingCopy(policy.id);
        if (existing) {
          navigate(workingCopyTarget(existing));
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const created = (await TenantFeePolicyApi.createDraft(buildAmendBody(policy))) as any;
        navigate(`${EDITOR_BASE}/${created.id}`);
      } catch (e) {
        if (isDraftExists(e)) {
          const existing = await findAmendWorkingCopy(policy.id);
          if (existing) {
            navigate(workingCopyTarget(existing));
            return;
          }
        }
        Message.error(resolveBillingError(e));
      }
    },
    [navigate]
  );
}
